import { type Analytics, AnalyticsBrowser } from '@june-so/analytics-next';
import accounting from 'accounting';
import { bind } from 'bind-decorator';
import createContext from 'bz-utilities/hooks/context-v2';
import { client } from 'connect/core/client';
import { AnalyticsEvents, analyticsEventMapping } from 'connect/core/enums/analytics-enum';
import sanitize from 'connect/core/utils/sanitize-html';
import { action, computed, observable, runInAction } from 'mobx';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import type { OnlineSettingsResponse } from 'tm3-core/api.dtos';
import { License, OnlineSettingsRequest } from 'tm3-core/api.dtos';

export const [ProviderStoreContext, useProviderStore] =
  createContext<ProviderStore>('ProviderStore');

class ProviderStore {
  @observable allowClassBooking = false;
  @observable allowRegister = false;
  @observable allowPayAtClinic = false;
  @observable classes = false;
  @observable clinical = false;
  @observable currency = '£';
  @observable license = new License();
  @observable message = '';
  @observable payments = false;
  @observable siteTitle = 'Connect Online Bookings';
  @observable showSiteTitle = false;
  @observable logoUrl = '';
  @observable appIconUrl = '';
  @observable skin = 'default';
  @observable title = '';
  @observable dobRequired = false;
  @observable cancellationPolicy: string = '';
  @observable termsAndConditions: string = '';
  @observable privacyPolicy: string = '';
  @observable customColourPrimary = '';
  @observable customColourSecondary = '';
  @observable customColourNav = '';
  @observable customColourNavText = '';
  @observable stripeEnabled = false;
  @observable squareEnabled: boolean = false;
  @observable clearAcceptEnabled: boolean = false;
  @observable useExtraAddressLine = false;
  @observable analyticsEnabled: boolean = false;
  @observable embeddedMode = false;
  @observable isSiteLive: boolean = false;
  @observable saveCardMessage = '';
  @observable googleTrackingId: string = null;
  @observable connectEnabled: boolean = false;
  @observable videoIntegration: string = 'NONE';
  @observable hasZoomPro: boolean = false;
  @observable stripeApiKey: string | null = null;
  @observable stripeAccount: string | null = null;
  @observable squareApplicationId: string | null = null;
  @observable isSquareSandboxMode: boolean = false;
  @observable tagManagerId: string = '';
  @observable unauthenticatedMessage = '';
  @observable addressLabels = {
    ADDR1: 'Address Line 1',
    ADDR2: 'Address Line 2',
    ADDR3: 'Address 3',
    ADDR4: 'Address Line 4',
    ADDR5: 'Address Line 5',
    ADDRPC: 'Postcode'
  };
  @observable isVHG: boolean = false;
  @observable recaptchaSiteKey: string | undefined;
  @observable recaptchaEnabled = false;
  @observable allowWaitingListPayments = false;
  @observable juneAnalytics: Analytics | undefined = undefined;
  @observable connectJuneAnalyticsWriteKey: string = '';
  @observable applePayEnabled = false;
  @observable currencyCode = 'GBP';
  @observable location = 'GBR';
  @observable useSSO = false;
  @observable ssoDomain?: string;
  @observable usingSimplifiedRegistration = false;

  @computed get isClinical() {
    return !this.license.isGDS;
  }

  constructor(embeddedMode: boolean = false) {
    this.embeddedMode = embeddedMode;
  }

  @bind
  public onLoad(embeddedMode: boolean, businessId?: number) {
    this.embeddedMode = embeddedMode;

    return client
      .get(new OnlineSettingsRequest({ businessId }))
      .then((res: OnlineSettingsResponse) => {
        const {
          allowClassBooking,
          allowRegister,
          allowPayAtClinic,
          classes,
          clinical,
          currency,
          license,
          message,
          payments,
          siteTitle,
          showSiteTitle,
          customColourPrimary,
          customColourSecondary,
          customColourNav,
          customColourNavText,
          logoUrl,
          appIconUrl,
          skin,
          title,
          dobRequired,
          stripeEnabled,
          squareEnabled,
          clearAcceptEnabled,
          googleTrackingId,
          isSiteLive,
          cancellationPolicy,
          termsAndConditions,
          privacyPolicy,
          useExtraAddressLine,
          stripeApiKey,
          stripeAccount,
          squareApplicationId,
          isSquareSandboxMode,
          connectEnabled,
          labels,
          googleTagManagerContainerId,
          videoIntegration,
          hasZoomPro,
          saveCardMessage,
          isVHG,
          recaptchaSiteKey,
          allowWaitingListPayments,
          connectJuneAnalyticsWriteKey,
          unauthenticatedMessage,
          useSSO,
          applePayEnabled,
          currencyCode,
          location,
          ssoDomain,
          usingSimplifiedRegistration
        } = res;

        runInAction(() => {
          this.isVHG = isVHG;
          this.allowClassBooking = allowClassBooking;
          this.allowRegister = allowRegister;
          this.allowPayAtClinic = allowPayAtClinic;
          this.classes = classes;
          this.clinical = clinical;
          this.currency = currency;
          this.license = license;
          this.message = message ? sanitize(message) : '';
          this.payments = payments;
          this.siteTitle = siteTitle;
          this.showSiteTitle = showSiteTitle;
          this.logoUrl = logoUrl;
          this.appIconUrl = appIconUrl;
          this.customColourPrimary = customColourPrimary;
          this.customColourSecondary = customColourSecondary;
          this.customColourNav = customColourNav;
          this.customColourNavText = customColourNavText;
          this.skin = skin;
          this.dobRequired = dobRequired;
          this.title = title;
          this.stripeEnabled = stripeEnabled;
          this.analyticsEnabled = !!googleTrackingId || !!googleTagManagerContainerId;
          this.googleTrackingId = googleTrackingId;
          this.isSiteLive = isSiteLive;
          this.cancellationPolicy = cancellationPolicy ? sanitize(cancellationPolicy) : '';
          this.termsAndConditions = termsAndConditions ? sanitize(termsAndConditions) : '';
          this.privacyPolicy = privacyPolicy ? sanitize(privacyPolicy) : '';
          this.useExtraAddressLine = useExtraAddressLine;
          accounting.settings.currency.symbol = currency;
          this.stripeApiKey = stripeApiKey;
          this.stripeAccount = stripeAccount;
          this.connectEnabled = connectEnabled;
          this.videoIntegration = videoIntegration;
          this.hasZoomPro = hasZoomPro;
          this.tagManagerId = googleTagManagerContainerId;
          this.squareEnabled = squareEnabled;
          this.clearAcceptEnabled = clearAcceptEnabled;
          this.isSquareSandboxMode = isSquareSandboxMode;
          this.squareApplicationId = squareApplicationId;
          this.recaptchaSiteKey = recaptchaSiteKey;
          this.saveCardMessage = saveCardMessage;
          this.recaptchaEnabled = !!recaptchaSiteKey;
          this.allowWaitingListPayments = allowWaitingListPayments;
          labels.forEach((dl) => (this.addressLabels[dl.id] = dl.text));
          this.connectJuneAnalyticsWriteKey = connectJuneAnalyticsWriteKey;
          this.unauthenticatedMessage = unauthenticatedMessage;
          this.useSSO = useSSO;
          this.applePayEnabled = applePayEnabled;
          this.currencyCode = currencyCode;
          this.location = location;
          this.ssoDomain = ssoDomain;
          this.usingSimplifiedRegistration = usingSimplifiedRegistration;
        });

        const cookies = document.cookie.split(';');

        const essentialCookie = cookies.find((cookie) => {
          return cookie.trim() === 'connectGdprEssentialOnly=true';
        });

        const consentCookie = cookies.find((cookie) => {
          return cookie.trim() === 'connectGdprConsent=true';
        });
        if (!!consentCookie && !essentialCookie) {
          this.enableAnalytics();
        }
      });
  }

  async enableAnalytics() {
    if (!!this.googleTrackingId) {
      ReactGA.initialize(this.googleTrackingId);
      ReactGA.ga('require', 'ecommerce');
      const path = window.location.pathname || '/';
      ReactGA.send({ hittype: 'pageview', page: path });
    }

    if (!!this.tagManagerId) {
      window.dataLayer = window.dataLayer || [];
      const tagManagerArgs = {
        gtmId: this.tagManagerId
      };

      TagManager.initialize(tagManagerArgs);
    }

    if (this.connectJuneAnalyticsWriteKey) {
      await this.loadJuneAnalytics(this.connectJuneAnalyticsWriteKey);

      if (!sessionStorage.getItem('First Visit')) {
        sessionStorage.setItem('First Visit', 'true');
        this.sendAnalyticsEvent(AnalyticsEvents.ConnectVisit);
      }
    }
  }
  disableAnalytics() {
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      if (cookie.trim().startsWith('_ga') || cookie.trim().startsWith('_gid')) {
        document.cookie = cookie + '; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    });
  }

  async loadJuneAnalytics(writeKey: string) {
    const response = await AnalyticsBrowser.load({ writeKey });

    if (!response) {
      console.error('June Analytics failed to initislise');
      return;
    }

    this.juneAnalytics = response[0];
  }

  @action.bound
  sendAnalyticsEvent(analyticsEvent: AnalyticsEvents, params?: any) {
    if (!this.analyticsEnabled || !analyticsEvent) return;

    const eventNames = analyticsEventMapping[analyticsEvent];

    if (eventNames.ga) ReactGA.event(eventNames.ga, params);

    if (!this.juneAnalytics) return;

    if (eventNames.june || !this.juneAnalytics) this.juneAnalytics.track(eventNames.june, params);
  }
}

export default ProviderStore;
